/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_LOCATION,
  DEFAULT_NAMESPACE,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { IconButton } from '@material-ui/core';
import {
  HeaderIconLinkRow,
  IconLinkVerticalProps,
  InfoCard,
  InfoCardVariants,
  Link,
} from '@backstage/core-components';
import React, { useCallback } from 'react';
import {
  ScmIntegrationIcon,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';
import {
  errorApiRef,
  useApi,
  useApp,
  useRouteRef,
} from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  getEntitySourceLocation,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { createFromTemplateRouteRef } from './routes';

import { AboutContent } from './AboutContent';
import { EntityCardTitle } from '../EntityCardTitle';
import CachedIcon from '@material-ui/icons/Cached';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import EditIcon from '@material-ui/icons/Edit';
import { isTemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { catalogEntityRefreshPermission } from '@backstage/plugin-catalog-common/alpha';
import { useCmdbRefresh, useGitLabRefresh } from '../../../hooks';

/**
 * Props for {@link EntityAboutCard}.
 *
 * @public
 */
export interface AboutCardProps {
  variant?: InfoCardVariants;
  /**
   * Whether to offer "View Source", the link to the file the entity is
   * declared in. On by default, as it is for a component or an api, whose
   * source is the catalog-info.yaml someone writes by hand.
   *
   * A page whose entity comes out of the CMDB turns it off: its source is the
   * CMDB, which the entity already links by name - see the CMDB link the
   * mapper puts on it - and the same page twice under two names says nothing
   * the second time.
   */
  showViewSource?: boolean;
}

/**
 * Exported publicly via the EntityAboutCard
 */
export function RTLAboutCard(props: Readonly<AboutCardProps>) {
  const { variant = 'fullHeight', showViewSource = true } = props;
  const app = useApp();
  const { entity } = useEntity();
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const catalogApi = useApi(catalogApiRef);
  const errorApi = useApi(errorApiRef);
  const refreshCmdb = useCmdbRefresh();
  const refreshGitLab = useGitLabRefresh();
  const templateRoute = useRouteRef(createFromTemplateRouteRef);
  const { allowed: canRefresh } = useEntityPermission(
    catalogEntityRefreshPermission,
  );

  const entitySourceLocation = getEntitySourceLocation(
    entity,
    scmIntegrationsApi,
  );
  const entityMetadataEditUrl =
    entity.metadata.annotations?.[ANNOTATION_EDIT_URL];

  const viewInSource: IconLinkVerticalProps = {
    label: 'View Source',
    disabled: !entitySourceLocation,
    icon: <ScmIntegrationIcon type={entitySourceLocation?.integrationType} />,
    href: entitySourceLocation?.locationTargetUrl,
  };

  const subHeaderLinks: IconLinkVerticalProps[] = showViewSource
    ? [viewInSource]
    : [];

  if (isTemplateEntityV1beta3(entity)) {
    const Icon = app.getSystemIcon('scaffolder') ?? CreateComponentIcon;

    const launchTemplate: IconLinkVerticalProps = {
      label: 'Launch Template',
      icon: <Icon />,
      disabled: !templateRoute,
      href: templateRoute?.({
        templateName: entity.metadata.name,
        namespace: entity.metadata.namespace ?? DEFAULT_NAMESPACE,
      }),
    };

    subHeaderLinks.push(launchTemplate);
  }

  const entityLocation = entity.metadata.annotations?.[ANNOTATION_LOCATION];
  // Limiting the ability to manually refresh to the less expensive locations
  const allowRefresh =
    entityLocation?.startsWith('url:') || entityLocation?.startsWith('file:');
  const refreshEntity = useCallback(async () => {
    try {
      const entityRef = stringifyEntityRef(entity);
      await catalogApi.refreshEntity(entityRef);
      // Re-processing alone would leave the entity on the data the provider
      // stored last time, so its source is re-read as well: the GitLab
      // project of an entity from a catalog-info.yaml, the CMDB otherwise.
      if (entity.metadata.annotations?.['gitlab.com/project-id']) {
        await refreshGitLab(entityRef);
      } else {
        await refreshCmdb();
      }
    } catch (e: any) {
      errorApi.post(e);
    }
  }, [catalogApi, refreshCmdb, refreshGitLab, errorApi, entity]);

  /**
   * What the card says under its heading: the description of the entity and
   * the links it offers.
   *
   * An entity with neither gets no subheader at all rather than an empty one -
   * InfoCard leaves off the band of space it would take up, the way it does
   * for the group card, which passes a description and nothing else.
   */
  const cardSubTitle = () => {
    if (!entity.metadata.description && subHeaderLinks.length === 0) {
      return undefined;
    }
    return (
      <div>
        {entity.metadata.description && (
          <div>{entity.metadata.description}</div>
        )}
        {subHeaderLinks.length > 0 && (
          <HeaderIconLinkRow links={subHeaderLinks} />
        )}
      </div>
    );
  };

  // The card is an InfoCard, the one the group card is built on as well,
  // rather than a Card of its own making: a hand-built header carries neither
  // the bold title nor the padding of a Backstage card, so the same entity
  // read as a product looked unlike the very same entity read as a team.
  return (
    <InfoCard
      variant={variant}
      title={
        // The card heads with what it is about rather than with the word
        // "About": the name of the entity, with the icon of its kind in
        // front of it the way the group card and every link to the entity
        // carry one. Which kind of card this is, is said by the icon.
        <EntityCardTitle
          entity={entity}
          title={entity.metadata.title ?? entity.metadata.name}
        />
      }
      subheader={cardSubTitle()}
      action={
        <>
          {allowRefresh && canRefresh && (
            <IconButton
              aria-label="Refresh"
              title="Schedule entity refresh"
              onClick={refreshEntity}
            >
              <CachedIcon />
            </IconButton>
          )}
          <IconButton
            component={Link}
            aria-label="Edit"
            disabled={!entityMetadataEditUrl}
            title="Edit Metadata"
            to={entityMetadataEditUrl ?? '#'}
          >
            <EditIcon />
          </IconButton>
        </>
      }
    >
      <AboutContent entity={entity} />
    </InfoCard>
  );
}
